import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Clock, Users, Brain, MessageSquare, Camera, RefreshCw, Shield, Zap } from 'lucide-react';

const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const benefits = [
    {
      icon: Clock,
      title: 'Ahorra 75+ horas al mes',
      description: 'Automatiza mensajes de seguimiento, recordatorios y respuestas a preguntas frecuentes.',
      highlight: '75+ hrs',
    },
    {
      icon: Users,
      title: 'Retención del 95%',
      description: 'Pacientes que se sienten acompañados vuelven. Y recomiendan.',
      highlight: '95%',
    },
    {
      icon: Brain,
      title: 'Habla exactamente como tú',
      description: 'No es un bot genérico. Tu IA aprende tu tono, tu estilo, tus frases favoritas.',
      highlight: 'Tu voz',
    },
    {
      icon: MessageSquare,
      title: 'Disponible 24/7 en WhatsApp',
      description: 'Responde al instante, a cualquier hora. El canal que tus pacientes ya usan.',
      highlight: '24/7',
    },
    {
      icon: Camera,
      title: 'Valida fotos de comida',
      description: 'Tu paciente envía foto de su plato, la IA verifica si cumple con el plan.',
      highlight: 'IA visual',
    },
    {
      icon: RefreshCw,
      title: 'Sustituciones inteligentes',
      description: '"No tengo pollo" → La IA sugiere alternativas equivalentes en calorías.',
      highlight: 'Adaptable',
    },
    {
      icon: Shield,
      title: 'Privacidad garantizada',
      description: 'Datos encriptados, cumplimiento HIPAA ready. Tus pacientes están seguros.',
      highlight: 'Seguro',
    },
    {
      icon: Zap,
      title: 'Configuración en minutos',
      description: 'Sin código, sin técnicos. Sube información y empieza a usar hoy.',
      highlight: '< 10 min',
    },
  ];

  return (
    <section id="beneficios" ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#F2F4F0]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            Beneficios
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            Todo lo que necesitas para
            <span className="text-[#2E5C41]"> escalar tu práctica</span>
          </h2>
          <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed">
            Más tiempo para consultas de alto valor. Menos trabajo administrativo. Mejores resultados para todos.
          </p>
        </motion.div>

        {/* Benefits Grid - Bento style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className={`p-6 bg-white rounded-2xl border border-[#2E5C41]/10 hover:border-[#2E5C41]/30 hover:shadow-lg transition-all duration-300 group ${
                index === 0 || index === 3 ? 'lg:col-span-2' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#2E5C41]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2E5C41] group-hover:scale-110 transition-all duration-300">
                  <benefit.icon className="w-6 h-6 text-[#2E5C41] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-cabinet font-bold text-[#1A2E24]">{benefit.title}</h3>
                    <span className="px-2 py-0.5 bg-[#2E5C41]/10 rounded text-xs font-bold text-[#2E5C41]">
                      {benefit.highlight}
                    </span>
                  </div>
                  <p className="text-sm text-[#4B5E53] leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social proof */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 md:mt-16 text-center"
        >
          <p className="text-[#4B5E53] text-sm">
            Únete a los nutricionistas que están transformando su práctica con IA
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;
